import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { AccessibilityPreferencesService } from './core/accessibility-preferences.service';
import {
  ENGLISH_CONTENT_ROUTE_ENTRIES,
  LOCALIZED_CONTENT_LANGUAGES,
  LOCALIZED_CONTENT_ROUTE_ENTRIES,
} from './content/content-route-manifest.generated';
import { LocalizationService } from './core/localization.service';
import { TranslatePipe } from './core/translate.pipe';
import { DsButtonDirective } from './ui/primitives/button.directive';
import { DsFooterComponent, DsHeaderComponent } from './ui/primitives/navigation.components';

interface HeaderCategoryLink {
  label: string;
  category: string;
  representativeSlug: string;
}

const HEADER_CATEGORY_LINKS: readonly HeaderCategoryLink[] = [
  { label: 'IQ basics', category: 'IQ essentials', representativeSlug: 'what-is-iq' },
  {
    label: 'Scores',
    category: 'IQ score interpretation',
    representativeSlug: 'iq-score-ranges-explained',
  },
  {
    label: 'Practice',
    category: 'Practice questions',
    representativeSlug: '20-pattern-recognition-questions',
  },
  {
    label: 'Tools',
    category: 'Interactive tools',
    representativeSlug: 'iq-percentile-calculator',
  },
];

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    TranslatePipe,
    DsButtonDirective,
    DsFooterComponent,
    DsHeaderComponent,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App {
  private readonly i18n = inject(LocalizationService);
  private readonly router = inject(Router);
  private readonly accessibilityPreferences = inject(AccessibilityPreferencesService);
  protected readonly mobileMenuOpen = signal(false);
  protected readonly languageChangePending = signal(false);
  protected readonly languageChangeNotice = signal('');
  protected readonly languages = this.i18n.languages;
  protected readonly currentLanguage = this.i18n.language;
  protected readonly aboutLink = computed(() => `/${this.i18n.language()}/about`);
  protected readonly contentLink = computed(() => {
    const language = this.i18n.language();
    return this.hasLocalizedContent(language) ? `/${language}/content` : '/en/content';
  });
  protected readonly categoryLinks = computed(() => {
    const language = this.i18n.language();

    return HEADER_CATEGORY_LINKS.map((link) => {
      const hasLocalizedCategory =
        language === 'en' ||
        LOCALIZED_CONTENT_ROUTE_ENTRIES.some(
          (entry) => entry.language === language && entry.slug === link.representativeSlug,
        );
      const contentLanguage = hasLocalizedCategory ? language : 'en';
      return {
        label: link.label,
        category: link.category,
        href: `/${contentLanguage}/content`,
      };
    });
  });

  protected closeMobileMenu(): void {
    this.mobileMenuOpen.set(false);
  }

  protected async changeLanguage(event: Event): Promise<void> {
    const language = (event.target as HTMLSelectElement).value;
    if (language === this.currentLanguage() || this.languageChangePending()) return;

    this.closeMobileMenu();
    this.languageChangeNotice.set('');
    this.languageChangePending.set(true);
    try {
      const target = this.localizedDestination(language, this.router.url);
      await this.i18n.setLanguage(language);
      if (target) {
        const navigated = await this.router.navigateByUrl(target);
        if (!navigated) this.setNavigationFailureNotice();
      }
    } catch {
      this.setNavigationFailureNotice();
    } finally {
      this.languageChangePending.set(false);
    }
  }

  private localizedDestination(language: string, currentUrl: string): string | null {
    const path = currentUrl.split(/[?#]/u)[0];
    const suffix = currentUrl.slice(path.length);
    const localizedRoute = path.match(/^\/[a-z]{2,3}\/(about|content)(?:\/([^/]+))?\/?$/u);
    if (!localizedRoute) return null;

    const [, section, slug] = localizedRoute;
    if (section === 'about') return `/${language}/about`;

    if (slug) {
      if (this.hasLocalizedArticle(language, slug)) {
        return `/${language}/content/${slug}`;
      }
      this.setUnavailableNotice(language);
      return null;
    }
    if (this.hasLocalizedContent(language)) {
      return `/${language}/content${suffix}`;
    }
    this.setUnavailableNotice(language);
    return null;
  }

  protected dismissLanguageNotice(): void {
    this.languageChangeNotice.set('');
  }

  private setUnavailableNotice(language: string): void {
    const name = this.languages().find((item) => item.code === language)?.nativeName ?? language;
    this.languageChangeNotice.set(
      `This page is not available in ${name} yet. Your language preference was saved and the current page remains open.`,
    );
  }

  private setNavigationFailureNotice(): void {
    this.languageChangeNotice.set(
      'Your language preference was saved, but this page could not be changed. The current page remains open.',
    );
  }

  private hasLocalizedContent(language: string): boolean {
    return language === 'en' || LOCALIZED_CONTENT_LANGUAGES.some((code) => code === language);
  }

  private hasLocalizedArticle(language: string, slug: string): boolean {
    if (language === 'en') {
      return ENGLISH_CONTENT_ROUTE_ENTRIES.some((entry) => entry.slug === slug);
    }
    return LOCALIZED_CONTENT_ROUTE_ENTRIES.some(
      (entry) => entry.language === language && entry.slug === slug,
    );
  }
}
